/**
 * El teléfono se pide en dos partes: el país en un selector y el número en el campo (24 ago 2026).
 *
 * **Por qué.** Hasta hoy los cinco formularios que piden teléfono eran texto libre con un
 * placeholder `+56 9 …` que es una sugerencia y nada más, y el servidor aceptaba ocho dígitos
 * pelados. Un número sin código de país **no se puede entregar**: medido contra Evolution, el mismo
 * teléfono responde `exists:false` como `979076970` y `exists:true` como `56979076970`. El `+` y los
 * espacios no importan —Evolution limpia lo no numérico—; **el código de país sí**.
 *
 * ⚠️ Y cuando falla, falla callado: Evolution devuelve 400, `agenda-salientes.sh` reintenta cinco
 * veces y marca `fallido`, y nadie lo mira. Por eso este cambio viene con la comprobación de
 * `mensajes_salientes` en `alertas.sh`: sin ella, el número mal guardado de ayer sigue sin avisar.
 *
 * **Por qué un selector visible y no asumir +56 en el servidor.** Asumirlo escribiría un número
 * plausible y equivocado para un contacto extranjero, y nadie se enteraría nunca. Un prefijo a la
 * vista hace la suposición corregible por quien la está tecleando.
 *
 * **De dónde sale la lista.** De `libphonenumber-js`, que es la misma que valida — así que el
 * selector no se puede desincronizar del validador. Los nombres los pone `Intl.DisplayNames` en
 * español (los 245 países tienen nombre; se comprobó). Actualizar la lista es `npm update
 * libphonenumber-js`: no hay tabla que mantener a mano ni descarga en tiempo de build.
 *
 * ⚠️ **El `<select>` es nativo a propósito.** Con 245 opciones, un combobox propio significa
 * reimplementar teclado, accesibilidad y el selector de rueda del teléfono. El nativo ya trae buscar
 * tecleando en escritorio y el picker del sistema en móvil.
 */
import {
  AsYouType, Metadata, getCountries, getCountryCallingCode, parsePhoneNumberFromString,
} from 'libphonenumber-js';

export const PAIS_POR_DEFECTO = 'CL';

/**
 * Los países ordenados alfabéticamente por su nombre en español.
 *
 * ⚠️ El orden se calcula con `localeCompare('es')` y no con `<`: sin eso «Ángola» y «Åland» caen
 * después de «Zimbabue», porque el orden de los puntos de código no es el del alfabeto.
 */
const listaPaises = () => {
  let dn = null;
  try { dn = new Intl.DisplayNames(['es'], { type: 'region' }); } catch { dn = null; }
  return getCountries()
    .map((iso) => ({
      iso,
      prefijo: getCountryCallingCode(iso),
      // Sin `Intl.DisplayNames` queda el código ISO: peor, pero legible y nunca vacío.
      nombre: (dn && dn.of(iso)) || iso,
    }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
};

let cachePaises = null;
export const paises = () => (cachePaises ??= listaPaises());

/**
 * El país principal de un código compartido. Doce códigos los comparten 51 países (+1 son 25, +44
 * son 4), y ahí «el primero alfabéticamente» sería *Antigua y Barbuda* para +1. La metadata de
 * libphonenumber lista el principal primero, que es la respuesta correcta y no una preferencia
 * nuestra.
 *
 * `actual` gana si comparte el código: quien ya eligió Canadá y teclea `+1` sigue en Canadá.
 */
function paisDePrefijo(prefijo, actual) {
  if (actual && getCountries().includes(actual)) {
    try { if (getCountryCallingCode(actual) === prefijo) return actual; } catch { /* ignorado */ }
  }
  try {
    const lista = new Metadata().countryCallingCodes()[prefijo];
    if (lista && lista.length) return lista[0];
  } catch { /* ignorado */ }
  return paises().find((p) => p.prefijo === prefijo)?.iso ?? null;
}

/**
 * A E.164 (`+56979076970`) o `null`. `null` significa «no se puede entregar», no «está vacío»:
 * quien llama distingue los dos casos mirando primero si hay texto.
 *
 * El corte es `isPossible()` y **no** `isValid()`: lo que hay que atajar es el número al que le
 * falta el país o le sobran/faltan dígitos, y eso `isPossible()` ya lo dice. `isValid()` compara
 * además contra los rangos nacionales asignados, así que un rango nuevo que la metadata todavía no
 * conoce dejaría a una persona real sin poder reservar — un falso negativo acá es una hora perdida.
 */
export function aE164(texto, pais = PAIS_POR_DEFECTO) {
  const crudo = String(texto ?? '').trim();
  if (!crudo) return null;
  let n = null;
  try { n = parsePhoneNumberFromString(crudo, pais || PAIS_POR_DEFECTO); } catch { return null; }
  return n && n.isPossible() ? n.number : null;
}

/**
 * El número tal como se lee, no tal como se guarda.
 *
 * ⚠️ La base guarda E.164 sin separadores —`56922334455`— porque es lo único que Evolution entrega,
 * y el portal lo estaba **mostrando así**: una tira de once dígitos en la tarjeta de un canal, en
 * la ficha de un contacto y en cada tarjeta del embudo. Un teléfono sin agrupar no se lee de un
 * vistazo ni se compara con el que alguien tiene en la mano, que es exactamente para lo que se
 * muestra. Lo agrupa `libphonenumber-js`, que es la misma librería que ya valida: no hay una
 * segunda tabla de formatos que pueda discrepar.
 *
 * Lo que no se puede interpretar sale **tal cual vino**, nunca vacío: un número raro guardado en la
 * base se tiene que poder ver para poder arreglarlo.
 */
export function telefonoLegible(valor, pais = PAIS_POR_DEFECTO) {
  const crudo = String(valor ?? '').trim();
  if (!crudo) return '';
  try {
    // Sin `+` y con código de país es lo que devuelve la base; `parsePhoneNumberFromString` lo lee
    // como nacional y le pega otro +56 adelante. El `+` se agrega cuando son sólo dígitos y alcanzan
    // para llevar prefijo internacional.
    const texto = /^\d{10,15}$/.test(crudo) ? `+${crudo}` : crudo;
    const n = parsePhoneNumberFromString(texto, pais || PAIS_POR_DEFECTO);
    return n && n.isPossible() ? n.formatInternational() : crudo;
  } catch { return crudo; }
}

/**
 * El campo: `<select>` de país + `<input>` de número, dentro de un `<span class="dv-tel">`.
 *
 * Devuelve el nodo y cuatro funciones. `valor()` da E.164 o `null`; `crudo()` da lo tecleado, que es
 * lo que hay que mostrar en un mensaje de error — repetirle a alguien el número normalizado que no
 * pudimos armar no le dice nada.
 */
export function campoTelefono({
  valor = null, pais = PAIS_POR_DEFECTO, requerido = false, placeholder = '9 1234 5678',
  clase = '', al = null,
} = {}) {
  // ⚠️ **`div` y no `span`, y no es indiferente.** Los dos envoltorios que lo hospedan declaran
  // `.ag-campo span { display: block }` y `.rp-campo span { display: block }` para su etiqueta, y esa
  // regla es (0,1,1) contra el (0,1,0) de `.dv-tel`: con un `span` el `display: flex` **nunca se
  // aplicaba**. En la página pública se veía bien igual, por casualidad —`select` e `input` quedan
  // `inline-block` y entraban en una línea—; en el modal del calendario, más angosto, el país
  // aparecía encima del número.
  const caja = document.createElement('div');
  caja.className = `dv-tel${clase ? ` ${clase}` : ''}`;

  const sel = document.createElement('select');
  sel.className = 'dv-tel-pais';
  sel.setAttribute('aria-label', 'Código de país');
  for (const p of paises()) {
    const op = document.createElement('option');
    op.value = p.iso;
    op.textContent = `${p.nombre} (+${p.prefijo})`;
    sel.appendChild(op);
  }

  const num = document.createElement('input');
  num.type = 'tel';
  // `inputmode="tel"` y no `numeric`: el teclado del teléfono trae `+`, `*` y `#`, que es lo que
  // alguien espera al escribir un número.
  num.inputMode = 'tel';
  num.autocomplete = 'tel-national';
  num.className = 'dv-tel-num';
  num.placeholder = placeholder;
  num.required = !!requerido;

  caja.appendChild(sel);
  caja.appendChild(num);

  const elegirPais = (iso) => {
    if (iso && sel.querySelector(`option[value="${iso}"]`)) {
      sel.value = iso;
      sel.title = sel.selectedOptions[0]?.textContent ?? '';
    }
  };

  /** Lo que se guardó puede venir en cualquier forma; se muestra separado en las dos partes. */
  const poner = (v) => {
    const crudo = String(v ?? '').trim();
    if (!crudo) { num.value = ''; return; }
    let n = null;
    try { n = parsePhoneNumberFromString(crudo, sel.value || PAIS_POR_DEFECTO); } catch { n = null; }
    if (n && n.countryCallingCode) {
      elegirPais(n.country && getCountryCallingCode(n.country) === n.countryCallingCode
        ? n.country : paisDePrefijo(n.countryCallingCode, sel.value));
      num.value = n.isPossible() ? n.formatNational() : n.nationalNumber;
    } else {
      // No se pudo interpretar: se muestra tal cual está guardado. Vaciar el campo escondería un
      // dato malo y haría que guardar sin tocar nada lo borrara.
      num.value = crudo;
    }
  };

  /**
   * ⚠️ **Lo que el usuario pidió y la única regla que corre en cada tecla**: si escribe o pega el
   * código de país dentro del campo del número, se lo saca de ahí y se mueve al selector. `+56`,
   * `0056` y `+56 9 7907 6970` son la misma intención.
   *
   * No se reformatea nada más mientras se teclea: `parsePhoneNumberFromString('9 7907')` ya devuelve
   * un número —incompleto— y reescribir el campo en cada tecla le mueve el cursor a quien está
   * escribiendo. El resto se normaliza al salir del campo.
   */
  const alEscribir = () => {
    const v = num.value;
    const soloDigitos = v.replace(/\D/g, '');
    let internacional = null;
    if (v.trimStart().startsWith('+')) internacional = `+${soloDigitos}`;
    // `00` es el prefijo internacional de la UITT y ningún número nacional empieza así. Se exige un
    // dígito más para no reaccionar a un `00` a medio escribir.
    else if (/^00\d/.test(soloDigitos)) internacional = `+${soloDigitos.slice(2)}`;
    if (internacional === null) return;

    const ayt = new AsYouType();
    ayt.input(internacional);
    const prefijo = ayt.getCallingCode();
    // Todavía es `+5`: no se sabe de qué país, así que no se toca nada.
    if (!prefijo) return;
    const resto = internacional.slice(1);
    if (!resto.startsWith(prefijo)) return;

    const iso = paisDePrefijo(prefijo, sel.value);
    if (iso) elegirPais(iso);
    num.value = resto.slice(prefijo.length);
  };

  /**
   * Al salir del campo se normaliza a la forma nacional del país elegido — pero **sólo si el número
   * es posible**. Reescribir uno que no se entendió sería cambiarle a alguien lo que tecleó justo
   * cuando está mal y necesita verlo para corregirlo.
   */
  const alSalir = () => {
    const crudo = num.value.trim();
    if (!crudo) return;
    let n = null;
    try { n = parsePhoneNumberFromString(crudo, sel.value || PAIS_POR_DEFECTO); } catch { n = null; }
    if (!n || !n.isPossible()) return;
    if (n.country && getCountryCallingCode(n.country) === n.countryCallingCode) elegirPais(n.country);
    else if (n.countryCallingCode) elegirPais(paisDePrefijo(n.countryCallingCode, sel.value));
    num.value = n.formatNational();
  };

  num.addEventListener('input', () => { alEscribir(); al?.(); });
  num.addEventListener('blur', alSalir);
  sel.addEventListener('change', () => { sel.title = sel.selectedOptions[0]?.textContent ?? ''; al?.(); });

  elegirPais(pais || PAIS_POR_DEFECTO);
  if (valor) poner(valor);

  return {
    nodo: caja,
    entrada: num,
    seleccion: sel,
    valor: () => aE164(num.value, sel.value),
    crudo: () => num.value.trim(),
    vacio: () => num.value.trim() === '',
    pais: () => sel.value,
    poner,
    enfocar: () => num.focus(),
  };
}

/**
 * El mensaje cuando no se pudo armar. Uno solo y en un solo lugar: los cinco formularios dicen lo
 * mismo, y decir «teléfono inválido» no le explica a nadie qué corregir.
 */
export const MSG_TELEFONO = 'Revisa el teléfono: elige el país y escribe el número sin el código, '
  + 'por ejemplo 9 1234 5678.';
